<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import IconsTextEditFeature from '../../icons/TextEditFeature.vue';
import { t } from '~/i18n';

const { hideLabel = false } = defineProps<{ hideLabel?: boolean }>();

// Typewriter animation for inline text editing card
const typewriterText = ref('');
const typewriterCursor = ref(true);
const typewriterCardRef = ref<HTMLElement | null>(null);
const typewriterFont = ref('Pacifico');
const typewriterIsRTL = ref(false);
const typewriterIsTyping = ref(true); // true = typing, false = deleting
let typewriterInterval: ReturnType<typeof setInterval> | null = null;
let typewriterObserver: IntersectionObserver | null = null;
const typewriterHasStarted = ref(false);

// Split text into characters with unique keys for animation
const typewriterCharacters = computed(() => {
  return typewriterText.value.split('').map((char, index) => ({
    char,
    key: `${currentLangIndex}-${currentWordIndex}-${index}`,
  }));
});

// 16 languages with their "edit" and "text" words, cycling through 4 fonts
const languageWords = computed(() => [
  {
    words: [
      t('landing_home.cards.text_edit.typewriter.en_edit'),
      t('landing_home.cards.text_edit.typewriter.en_text'),
    ],
    font: 'Pacifico',
    rtl: false,
  }, // English
  { words: ['編集', 'テキスト'], font: 'Cherry Bomb One', rtl: false }, // Japanese
  { words: ['편집', '텍스트'], font: 'Funnel Sans', rtl: false }, // Korean
  { words: ['editar', 'texto'], font: 'Libre Baskerville', rtl: false }, // Spanish
  { words: ['éditer', 'texte'], font: 'Pacifico', rtl: false }, // French
  { words: ['bearbeiten', 'Text'], font: 'Cherry Bomb One', rtl: false }, // German
  { words: ['editar', 'texto'], font: 'Funnel Sans', rtl: false }, // Portuguese
  { words: ['modifica', 'testo'], font: 'Libre Baskerville', rtl: false }, // Italian
  { words: ['تحرير', 'نص'], font: 'Pacifico', rtl: true }, // Arabic (RTL)
  { words: ['संपादित', 'पाठ'], font: 'Cherry Bomb One', rtl: false }, // Hindi
  { words: ['แก้ไข', 'ข้อความ'], font: 'Funnel Sans', rtl: false }, // Thai
  { words: ['chỉnh sửa', 'văn bản'], font: 'Libre Baskerville', rtl: false }, // Vietnamese
  { words: ['bewerken', 'tekst'], font: 'Pacifico', rtl: false }, // Dutch
  { words: ['edytuj', 'tekst'], font: 'Cherry Bomb One', rtl: false }, // Polish
  { words: ['düzenle', 'metin'], font: 'Funnel Sans', rtl: false }, // Turkish
  { words: ['redigera', 'text'], font: 'Libre Baskerville', rtl: false }, // Swedish
]);

let currentLangIndex = 0;
let currentWordIndex = 0;
let currentCharIndex = 0;
let isDeleting = false;

const runTypewriterAnimation = () => {
  if (typewriterInterval) {
    clearInterval(typewriterInterval);
  }

  const typeSpeed = 80; // Faster typing
  const deleteSpeed = 50; // Faster deleting
  const pauseBeforeDelete = 1200; // Pause after finishing a word
  const pauseBeforeNextWord = 300; // Pause between words in same language
  const pauseBetweenLanguages = 1000; // Longer pause between different languages

  const animate = () => {
    const currentLang = languageWords.value[currentLangIndex];
    if (!currentLang) return;

    const currentWord = currentLang.words[currentWordIndex] ?? '';
    typewriterFont.value = currentLang.font;
    typewriterIsRTL.value = currentLang.rtl ?? false;

    if (!isDeleting) {
      typewriterIsTyping.value = true;
      // Typing
      if (currentCharIndex < currentWord.length) {
        typewriterText.value = currentWord.substring(0, currentCharIndex + 1);
        currentCharIndex++;
        typewriterInterval = setTimeout(animate, typeSpeed);
      } else {
        // Finished typing, pause then delete
        typewriterInterval = setTimeout(() => {
          isDeleting = true;
          typewriterIsTyping.value = false;
          animate();
        }, pauseBeforeDelete);
      }
    } else {
      typewriterIsTyping.value = false;
      // Deleting
      if (currentCharIndex > 0) {
        currentCharIndex--;
        typewriterText.value = currentWord.substring(0, currentCharIndex);
        typewriterInterval = setTimeout(animate, deleteSpeed);
      } else {
        // Finished deleting, move to next word
        isDeleting = false;
        typewriterIsTyping.value = true;
        currentWordIndex++;

        // If finished both words in current language, move to next language
        if (currentWordIndex >= currentLang.words.length) {
          currentWordIndex = 0;
          currentLangIndex = (currentLangIndex + 1) % languageWords.value.length;
          // Longer pause when switching languages
          typewriterInterval = setTimeout(animate, pauseBetweenLanguages);
        } else {
          // Shorter pause between words in same language
          typewriterInterval = setTimeout(animate, pauseBeforeNextWord);
        }
        return;
      }
    }
  };

  // Reset state
  currentLangIndex = 0;
  currentWordIndex = 0;
  currentCharIndex = 0;
  isDeleting = false;
  typewriterText.value = '';
  typewriterFont.value = languageWords.value[0]?.font ?? 'Pacifico';
  typewriterIsRTL.value = languageWords.value[0]?.rtl ?? false;

  animate();
};

const setupTypewriterObserver = () => {
  if (!typewriterCardRef.value) return;

  typewriterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !typewriterHasStarted.value) {
          typewriterHasStarted.value = true;
          runTypewriterAnimation();
        }
      });
    },
    { threshold: 0.5 }
  );

  typewriterObserver.observe(typewriterCardRef.value);
};

// Cursor blink
let cursorBlinkInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  setupTypewriterObserver();

  // Cursor blink animation
  cursorBlinkInterval = setInterval(() => {
    typewriterCursor.value = !typewriterCursor.value;
  }, 530);
});

onUnmounted(() => {
  if (typewriterInterval) {
    clearTimeout(typewriterInterval);
  }
  if (typewriterObserver) {
    typewriterObserver.disconnect();
  }
  if (cursorBlinkInterval) {
    clearInterval(cursorBlinkInterval);
  }
});
</script>

<template>
  <div
    ref="typewriterCardRef"
    class="group relative overflow-hidden flex flex-col md:flex-row"
    :class="hideLabel ? 'h-full' : 'rounded-3xl border border-black/5 bg-white'"
  >
    <div v-if="!hideLabel" class="p-6 md:p-8 md:w-1/3 flex flex-col justify-center">
      <IconsTextEditFeature class="w-5 h-5 text-black mb-4" />
      <p class="text-lg md:text-xl leading-[1.4]">
        <span class="text-black">Edit Text Directly.</span>
        <span class="text-black/50"> Click, type, instant changes.</span>
      </p>
    </div>
    <div
      class="relative md:flex-1 overflow-hidden"
      :class="hideLabel ? 'h-full' : 'h-80 md:h-[560px]'"
    >
      <div class="absolute inset-0 flex items-center justify-center">
        <!-- Text selection box with anchor points -->
        <div
          class="text-selection-box relative inline-block px-4 py-2 transition-[direction] duration-300"
          :style="{ direction: typewriterIsRTL ? 'rtl' : 'ltr' }"
        >
          <!-- Selection border -->
          <div class="absolute inset-0 border-2 border-[#FE713B] pointer-events-none"></div>
          <!-- Corner anchors -->
          <div
            class="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-[#FE713B] rounded-sm"
          ></div>
          <div
            class="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-[#FE713B] rounded-sm"
          ></div>
          <div
            class="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-[#FE713B] rounded-sm"
          ></div>
          <div
            class="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-[#FE713B] rounded-sm"
          ></div>
          <!-- Typewriter text container -->
          <div class="inline-flex items-center">
            <!-- Text with character-by-character animation -->
            <span
              class="text-[6rem] md:text-[8rem] text-black inline-flex"
              :style="{ fontFamily: typewriterFont }"
            >
              <TransitionGroup name="char">
                <span
                  v-for="item in typewriterCharacters"
                  :key="item.key"
                  class="inline-block char-item"
                  >{{ item.char }}</span
                >
              </TransitionGroup>
            </span>
            <!-- Cursor -->
            <span
              class="text-[6rem] md:text-[8rem] font-thin text-[#FE713B] typewriter-cursor"
              :class="{ 'opacity-0': !typewriterCursor }"
              >|</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Cherry+Bomb+One&family=Pacifico&family=Funnel+Sans&family=Libre+Baskerville&display=swap');

/* Typewriter cursor */
.typewriter-cursor {
  transition: opacity 0.1s ease;
  margin-left: -2px;
}

/* Character animation */
.char-item {
  display: inline-block;
  white-space: pre;
}

.char-enter-active {
  transition: opacity 0.08s ease-out;
}

.char-leave-active {
  transition: opacity 0.05s ease-out;
}

.char-enter-from {
  opacity: 0;
}

.char-leave-to {
  opacity: 0;
}

/* Text selection box */
.text-selection-box {
  min-width: 80px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
